import { ChangeDetectionStrategy, Component, inject, input } from '@angular/core';
import { AsyncPipe } from '@angular/common';
import { faArrowsRotate, faSparkles } from '@fortawesome/pro-solid-svg-icons';
import { ButtonComponent, IconComponent, TooltipDirective } from '@pure/hive';
import { MarkdownComponent } from 'ngx-markdown';
import { CopilotPageSummaryService } from '../services/copilot-page-summary.service';
import { COPILOT_EMPTY_PLACEHOLDER_TEXT } from '../side-panel/copilot-side-panel.component';

const SKELETON_LINES = [0, 1, 2];

/**
 * Completeness disclaimer shown beneath a rendered summary. The summary is generated from the
 * page context captured at open time, so it reflects only what is currently on screen and can
 * miss rows hidden by pagination or filters — this tells the reader that and points them to a
 * full chat for detail. Static UI text (not part of the LLM prompt) so it always appears and
 * reads consistently. Requested by PM after a paginated Planning table led to an incomplete
 * read (CLOUD-231829).
 */
export const PAGE_SUMMARY_DISCLAIMER =
    'This summary is based on what is currently shown on this page and may not account for ' +
    'pagination, filters, or other data not visible here. Ask me for a detailed analysis.';

/**
 * Renders the Copilot side-panel "page summary" in place of the empty-state prompt.
 * While the summary is generating it shows a loading skeleton (matching page-suggested
 * questions); when ready it renders the markdown summary; on idle/error — or when the
 * feature is disabled — it falls back to the same "Ask AI Copilot anything…" placeholder
 * the side panel shows by default.
 *
 * The `enabled` input (rather than an `@if` around this element in the host) gates the
 * feature: `<ng-content select>` does not match projected nodes wrapped in `@if`, so this
 * component is always projected and decides internally what to render.
 */
@Component({
    selector: 'copilot-page-summary',
    standalone: true,
    templateUrl: './copilot-page-summary.component.html',
    styleUrl: './copilot-page-summary.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    preserveWhitespaces: false,
    imports: [AsyncPipe, ButtonComponent, IconComponent, MarkdownComponent, TooltipDirective],
})
export class CopilotPageSummaryComponent {
    private readonly summaryService = inject(CopilotPageSummaryService);

    /** When false, always show the plain placeholder text (feature flag off). */
    readonly enabled = input(false);

    protected readonly state$ = this.summaryService.state$;
    protected readonly skeletonLines = SKELETON_LINES;
    protected readonly faSparkles = faSparkles;
    protected readonly faRefresh = faArrowsRotate;
    protected readonly placeholder = COPILOT_EMPTY_PLACEHOLDER_TEXT;
    protected readonly disclaimer = PAGE_SUMMARY_DISCLAIMER;

    /**
     * Regenerate the summary for the current page. Offered only in the `ready` state, mirroring
     * the page-suggested-questions refresh: while loading there is nothing yet to re-roll, and
     * the idle/error states render the plain placeholder with no header to hang the button on.
     */
    protected onRefresh(): void {
        this.summaryService.refresh();
    }
}
